import React from 'react';
import { Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import config from '../config';
import appStoreBadge from '../assets/images/app_store.svg';
import mobileLandingScreen from '../assets/images/mobile-landing-screen.png';
import { green } from '../theme';
import { color } from '../theme/tokens';

// ─── Design tokens ───────────────────────────────────────────────────────────
// The pre-brief palette carried two near-identical greens here — one for h1,
// one for h2/accents/CTA. The brief locks a single text green, so both resolve
// to `evergreen`; the names are kept because they still say which LandingPage
// role each usage came from.
const HEADING_GREEN = color.evergreen;
const DARK_GREEN   = color.evergreen;
const ACCENT_GREEN = color.accent;   // gradient start on the h1 emphasis
const DEEP_GREEN   = green.ctaDark;
const PAGE_BG      = color.paper;

// ─── Dot separator for trust bar ──────────────────────────────────────────────
function Dot() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        width: '0.1875rem',
        height: '0.1875rem',
        borderRadius: '50%',
        backgroundColor: 'rgba(0,0,0,0.2)',
        flexShrink: 0,
        mx: '0.25rem',
      }}
    />
  );
}

// ─── Google Play badge ────────────────────────────────────────────────────────
function GooglePlayBadge({ height = { xs: '3rem', md: '3.5rem' } }) {
  return (
    <Box
      component="a"
      href={config.APP_STORE_ANDROID}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Get it on Google Play"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: { xs: '0.75rem', md: '0.875rem' },
        // Vendor brand mark — the black-and-white Google Play badge, whose
        // colours Google's badge guidelines fix. Same ruling as the four logo
        // path fills below and the Apple wordmark: not design-system colours,
        // deliberately left as literals. Do not sweep these onto tokens.
        backgroundColor: '#000',
        color: '#fff',
        borderRadius: '0.5rem',
        px: { xs: '1.125rem', md: '1.35rem' },
        height,
        textDecoration: 'none',
        border: '0.0625rem solid rgba(255,255,255,0.15)',
        boxShadow: '0 0.125rem 0.5rem rgba(0,0,0,0.22)',
        transition: 'transform 0.15s ease, opacity 0.15s ease',
        '&:hover': { transform: 'translateY(-0.125rem)', opacity: 0.88 },
      }}
    >
      <Box
        component="svg"
        sx={{ width: { xs: '1.375rem', md: '1.55rem' }, height: { xs: '1.5rem', md: '1.7rem' }, flexShrink: 0 }}
        viewBox="0 0 24 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M1.5 0.5L13.5 12.5L1.5 24.5V0.5Z" fill="#EA4335"/>
        <path d="M20.5 9L13.5 12.5L20.5 16L23.5 14C24.5 13.4 24.5 11.6 23.5 11L20.5 9Z" fill="#FBBC04"/>
        <path d="M1.5 0.5L13.5 12.5L20.5 9L5.5 0.7C3.7 -0.3 1.5 0.5 1.5 0.5Z" fill="#4285F4"/>
        <path d="M1.5 24.5L13.5 12.5L20.5 16L5.5 23.3C3.7 24.3 1.5 24.5 1.5 24.5Z" fill="#34A853"/>
      </Box>
      <Box>
        <Typography sx={{ fontSize: { xs: '0.625rem', md: '0.7rem' }, opacity: 0.6, lineHeight: 1, letterSpacing: '0.01875rem' }}>Get it on</Typography>
        <Typography sx={{ fontSize: { xs: '1rem', md: '1.125rem' }, fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' }}>Google Play</Typography>
      </Box>
    </Box>
  );
}

// ─── iPhone frame ─────────────────────────────────────────────────────────────
function IPhoneHero() {
  return (
    <Box
      aria-hidden="true"
      sx={{
        width: { xs: '14.25rem', md: '16.25rem' },
        borderRadius: '2.75rem',
        border: `0.5rem solid ${DEEP_GREEN}`,
        backgroundColor: DEEP_GREEN,
        boxShadow: `0 2rem 4.5rem rgba(10,37,24,0.28), 0 0 0 0.0625rem rgba(10,37,24,0.2), inset 0 0.0625rem 0 rgba(255,255,255,0.08)`,
        overflow: 'hidden',
        position: 'relative',
        flexShrink: 0,
      }}
    >
      {/* Mobile landing page screenshot inside the phone frame. */}
      <Box
        sx={{
          height: { xs: '29.25rem', md: '34rem' },
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={mobileLandingScreen}
          alt="myStack mobile landing page"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'top center',
            display: 'block',
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function DownloadPage() {
  return (
    <>
      <Helmet>
        <title>Download myStack — AI Supplement App for iOS & Android</title>
        <meta
          name="description"
          content="Download the myStack health intelligence app. Build supplement stacks matched to your health profile and backed by human clinical research."
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <Box
        sx={{
          backgroundColor: PAGE_BG,
          minHeight: { xs: 'calc(100vh - 10rem)', md: 'calc(100vh - 6rem)' },
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Hero ──────────────────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            pt: { xs: '2.5rem', md: '4.5rem' },
            pb: { xs: '3rem', md: '4rem' },
            px: { xs: '1rem', md: '2rem' },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '68rem',
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              justifyContent: { md: 'center' },
              gap: { xs: '2.5rem', md: '3.5rem' },
            }}
          >
            {/* ── Left: copy ── */}
            <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: '34rem' }, textAlign: { xs: 'center', md: 'left' } }}>

              {/* Headline */}
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2.1rem', sm: '2.65rem', md: '3.15rem' },
                  lineHeight: 1.12,
                  letterSpacing: '-0.03em',
                  color: HEADING_GREEN,
                  mb: '1.25rem',
                }}
              >
                Supplement stacks built for your biology,{' '}
                <Box
                  component="em"
                  sx={{
                    fontStyle: 'italic',
                    background: `linear-gradient(135deg, ${ACCENT_GREEN} 0%, ${DARK_GREEN} 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  not just your goals.
                </Box>
              </Typography>

              {/* Subhead */}
              <Typography
                sx={{
                  fontSize: { xs: '1rem', md: '1.05rem' },
                  fontWeight: 500,
                  lineHeight: 1.7,
                  color: 'text.secondary',
                  maxWidth: { md: '31.25rem' },
                  mb: '2.25rem',
                }}
              >
                myStack matches every supplement recommendation to your health profile and shows the research behind each one.
              </Typography>

              {/* ── Download cluster ── */}
              <Box
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', md: '30rem' },
                  display: 'grid',
                  rowGap: '1.375rem',
                  justifyItems: { xs: 'center', md: 'start' },
                }}
              >
                {/* CTA prompt above the badges */}
                <Typography
                  sx={{
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                    fontWeight: 700,
                    color: DARK_GREEN,
                    letterSpacing: '-0.01em',
                  }}
                >
                  Download now to cut through the noise
                </Typography>

                {/* Store badges */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '0.875rem',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  <Box
                    component="a"
                    href={config.APP_STORE_IOS}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download on the App Store"
                    sx={{
                      display: 'inline-block',
                      transition: 'transform 0.15s ease, opacity 0.15s ease',
                      '&:hover': { transform: 'translateY(-0.125rem)', opacity: 0.88 },
                    }}
                  >
                    <Box
                      component="img"
                      src={appStoreBadge}
                      alt="Download on the App Store"
                      sx={{ height: { xs: '3rem', md: '3.5rem' }, display: 'block' }}
                    />
                  </Box>
                  <GooglePlayBadge />
                </Box>

                {/* Trust bar — stars · zero ads · private · credentials */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '0.25rem',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}
                >
                  {/* Amber left as a literal by ruling (STACK-508). BRAND_GUIDELINES
                      §13 forbids star ratings outright and DESIGN_DOC §11 tracks
                      them as debt — recolouring would polish an element that is
                      meant to be removed, not restyled. */}
                  {['★','★','★','★','★'].map((s, i) => (
                    <Typography key={i} component="span" sx={{ color: '#f59e0b', fontSize: '0.8rem', lineHeight: 1 }}>{s}</Typography>
                  ))}
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: DARK_GREEN, ml: '0.125rem' }}>
                    5.0 App Store
                  </Typography>
                  <Dot />
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>Zero ads</Typography>
                  <Dot />
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>Your data stays private</Typography>
                  <Dot />
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', fontWeight: 500 }}>Founded by a pharmacist with a PhD in nutrition</Typography>
                </Box>
              </Box>
            </Box>

            {/* ── Right: iPhone ── */}
            <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
              <IPhoneHero />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}
